/**
 * Tenant-assignable role catalog (RBAC FE WS-B).
 *
 * Mirrors the backend role set a tenant Admin can invite into / change a user
 * to: `admin`, `recruiter`, `hiring_manager`. The deprecated `member` role was
 * migrated to `recruiter` in RBAC WS-1 and is GONE — it must not appear in any
 * selectable control. `owner` exists server-side for back-compat but is never
 * tenant-assignable, so it is omitted from this picker catalog (it is still a
 * valid *displayed* role — see `roleLabel`).
 */

/** Roles a tenant Admin can assign via invite / role-change controls. */
export const ASSIGNABLE_ROLES = ["admin", "recruiter", "hiring_manager"] as const;

/** Union of tenant-assignable role values. */
export type TenantRole = (typeof ASSIGNABLE_ROLES)[number];

/** Human-readable label for a role value (incl. non-assignable `owner`). */
export function roleLabel(role: string): string {
  switch (role) {
    case "admin":
      return "Admin";
    case "recruiter":
      return "Recruiter";
    case "hiring_manager":
      return "Hiring Manager";
    case "owner":
      return "Owner";
    default:
      return role;
  }
}

/**
 * Roles that count as "admin-or-above" for last-admin protection (#10).
 * `owner` is included because demoting the sole owner/admin is the protected
 * case the backend `role_changes.py` guard enforces.
 */
export const ADMIN_ROLES: ReadonlySet<string> = new Set(["admin", "owner"]);
